// Renders a PageDoc as a real web page (React/CSS) — the public render path AND
// the builder's WYSIWYG preview, so what an editor sees equals what ships.
// Leaf blocks reuse the newsletter style functions; product blocks reuse the
// extracted storefront components; the cart routes through addToCartAndNotify so
// the shared <CartPill> works everywhere.
import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type {
  PageDoc, PageTheme, SectionBlock, RowBlock, ColumnBlock, LeafBlock,
  HeroBlock, ProductEmbedBlock, ProductGridBlock, MiniNavBlock, LibraryBlock,
} from '../../../shared/pageBlocks';
import { buildMergedGallery, buildVariantHeroIndex, themeProductHeaderStyle } from '../../../shared/pageBlocks';
import type { TextBlock, ButtonBlock, LineBlock, SpacerBlock } from '../../../shared/newsletterBlocks';
import {
  textBlockStyle,
  buttonBlockStyle, buttonStyle, lineBlockStyle, lineStyle, spacerStyle,
} from '../../../shared/newsletterBlocks';
import {
  pageRootStyle, sectionOuterStyle, sectionInnerStyle, rowStyle, columnStyle,
  heroOuterStyle, heroOverlayStyle, heroContentStyle, asNewsletterTheme,
} from './leafWebStyles';
import { TextLeafContent, HeroRichText, heroCtaStyle, LibraryLeaf, MiniNavLeaf, SmartLink } from './leafDisplay';
import { useBelow } from './useViewport';
import { useProgressiveTierBgImage } from './useProgressiveTierSrc';
import { coverViewportPx } from '@/utils/abrQuality';
import {
  ProductDetail, ProductCard, MobileFullScreenProductCard,
  fetchAllProducts, addToCartAndNotify, shouldShow, inCollection, sortProducts, findProduct,
  type StoreProduct,
} from '@/features/store';
import { type ProductGalleryConfig } from '@/services/pageBuilderService';

// server/mediaCdn.js's attachSiteMediaVariants() stashes these as sibling
// fields directly on `hero`/`section` leaf JSON in PUBLIC page-doc responses
// when a ready lightweight variant exists — intentionally not part of the
// shared pageBlocks/newsletterBlocks data model since they're a purely
// additive, may-be-absent rendering enhancement, not authored content. See
// the matching LibraryBlockWithVariants in leafDisplay.tsx (the `library`
// leaf — the legacy standalone `image`/`video` leaves are migrated away on
// every read, see migrateLegacyBlocks in shared/pageBlocks.js).
type HeroBlockWithVariant = HeroBlock & { bgImageVariantPlaceholderSrc?: string; bgImageVariantSrc?: string; bgImageVariantLgSrc?: string };
type SectionBlockWithVariant = SectionBlock & { bgImageVariantPlaceholderSrc?: string; bgImageVariantSrc?: string; bgImageVariantLgSrc?: string };

// Per-product canonical galleries (the assets the user set on each product's
// detail page), provided at the page root so cards + card/fullscreen embeds show
// the SAME assets as the detail gallery. Empty default → cards use Shopify images.
const ProductGalleriesCtx = React.createContext<Record<string, ProductGalleryConfig>>({});
// The merged gallery (Shopify + custom assets, in the user's order) for a product,
// or undefined when it has no customization. Pure — safe to call inside a map.
function mergedGalleryFor(galleries: Record<string, ProductGalleryConfig>, product: StoreProduct) {
  // Keyed by stable product ID first (rename-proof), handle as fallback.
  const cfg = galleries[product.id] ?? galleries[product.handle];
  return cfg ? buildMergedGallery(product, cfg) : undefined;
}

interface PageRendererProps {
  doc: PageDoc;
  /** Click handler for a product card/grid item (e.g. navigate to its page). */
  onProductClick?: (handle: string) => void;
  /** Force mobile layout (for the editor's mobile-preview frame); falls back to
   *  the live window width when undefined. */
  isMobile?: boolean;
  /** Product handles that have a builder page — sorted to the top of grids. */
  productPageHandles?: ReadonlySet<string>;
  /** Per-product canonical galleries so cards/embeds match the detail gallery. */
  productGalleries?: Record<string, ProductGalleryConfig>;
}

const Hero: React.FC<{ hero: HeroBlock; theme: PageTheme; onNavigate?: (t: string) => void }> = ({ hero, theme, onNavigate }) => {
  const heroVariants = hero as HeroBlockWithVariant;
  const resolvedBgImage = useProgressiveTierBgImage({
    placeholder: heroVariants.bgImageVariantPlaceholderSrc,
    small: heroVariants.bgImageVariantSrc, large: heroVariants.bgImageVariantLgSrc, original: hero.bgImage,
    // Full-bleed cover background: the larger viewport axis is the limiting
    // dimension for the auto-quality display cap.
    displayWidthPx: coverViewportPx(),
    // Full-bleed and often the LCP element — the ~1600px large tier alone can
    // look soft on a high-DPI/ultra-wide display, so this keeps going to the
    // true original once large has rendered, instead of capping there forever.
    finalTier: 'original',
  });
  const heroForStyle = resolvedBgImage !== hero.bgImage ? { ...hero, bgImage: resolvedBgImage } : hero;
  return (
    <div style={heroOuterStyle(heroForStyle)}>
      <div style={heroOverlayStyle(hero)} />
      <div style={heroContentStyle(hero)}>
        {/* Heading + subheading come from the shared leafDisplay so the builder's
            unselected hero renders identically. The CTA is a navigating link here
            (the editor uses an inert span). */}
        <HeroRichText hero={hero} theme={theme} />
        {hero.ctaLabel && (
          <SmartLink target={hero.ctaHref} onNavigate={onNavigate} style={heroCtaStyle(theme)}>
            {hero.ctaLabel}
          </SmartLink>
        )}
      </div>
    </div>
  );
};

const ProductEmbed: React.FC<{
  block: ProductEmbedBlock;
  theme: PageTheme;
  products: StoreProduct[];
  isMobile: boolean;
  onProductClick?: (handle: string) => void;
}> = ({ block, theme, products, isMobile, onProductClick }) => {
  const galleries = useContext(ProductGalleriesCtx);
  const product = findProduct(products, { id: block.productId, handle: block.handle });
  // Canonical gallery for card/fullscreen variants (same assets as detail). The
  // detail variant builds from its own block below (it IS the canonical source).
  const cardGallery = product ? mergedGalleryFor(galleries, product) : undefined;
  if (!product) {
    // Visitor-facing: never surface the raw Shopify handle — a stale/renamed
    // handle is an internal identifier, not a product name.
    return <div style={{ padding: 24, textAlign: 'center', opacity: 0.6 }}>This product is currently unavailable.</div>;
  }
  if (block.variant === 'fullscreen') {
    return <MobileFullScreenProductCard product={product} gallery={cardGallery} showVideos={block.showVideos !== false} onLearnMore={() => onProductClick?.(product.id)} onAddToCart={addToCartAndNotify} />;
  }
  if (block.variant === 'card') {
    return (
      <div style={{ maxWidth: 360, margin: '0 auto' }}>
        <ProductCard product={product} gallery={cardGallery} showVideos={block.showVideos !== false} onClick={() => onProductClick?.(product.id)} onAddToCart={addToCartAndNotify} />
      </div>
    );
  }
  const galleryImages = buildMergedGallery(product, {
    showShopifyImages: block.showShopifyImages,
    extraImages: block.extraImages,
    galleryOrder: block.galleryOrder,
  });
  return (
    <ProductDetail
      product={product}
      onBack={() => onProductClick?.(product.id)}
      onAddToCart={addToCartAndNotify}
      isMobile={isMobile}
      showBack={false}
      layout={block.layout ?? 'stacked'}
      galleryImages={galleryImages}
      variantHeroIndex={buildVariantHeroIndex(galleryImages, block.variantHeroImages)}
      autoAdvance={block.galleryAutoAdvance}
      autoAdvanceSeconds={block.galleryAutoAdvanceSeconds}
      bgColor={block.bgColor}
      headerStyle={themeProductHeaderStyle(theme)}
    />
  );
};

const ProductGrid: React.FC<{
  block: ProductGridBlock;
  products: StoreProduct[];
  onProductClick?: (handle: string) => void;
  productPageHandles?: ReadonlySet<string>;
}> = ({ block, products, onProductClick, productPageHandles }) => {
  const galleries = useContext(ProductGalleriesCtx);
  const items = useMemo(() => {
    const visible = products.filter(shouldShow);
    // Explicit lists keep the author's chosen order; auto/collection grids get the
    // default sort (built products first, then price high → low).
    if (block.collection) return sortProducts(visible.filter((p) => inCollection(p, block.collection!)), productPageHandles);
    if (block.handles === 'all') return sortProducts(visible, productPageHandles);
    // Prefer the stable product-ID list (rename-proof); fall back to handles for
    // legacy grids not yet migrated.
    const ids = block.productIds && block.productIds.length ? new Set(block.productIds) : null;
    const wantedHandles = new Set(block.handles);
    return visible.filter((p) => (ids ? ids.has(p.id) : wantedHandles.has(p.handle)));
  }, [products, block.handles, block.productIds, block.collection, productPageHandles]);

  const minCol = Math.max(160, Math.floor(1100 / block.columns));
  const grid: CSSProperties = {
    display: 'grid',
    // min(…, 100%) guarantees a single column never overflows a narrow frame.
    gridTemplateColumns: `repeat(auto-fill, minmax(min(${minCol}px, 100%), 1fr))`,
    gap: 20,
    width: '100%',
  };
  return (
    <div style={grid}>
      {items.map((p) => (
        <ProductCard key={p.id} product={p} gallery={mergedGalleryFor(galleries, p)} showVideos={block.showVideos !== false} onClick={() => onProductClick?.(p.id)} onAddToCart={addToCartAndNotify} />
      ))}
    </div>
  );
};

const Leaf: React.FC<{
  leaf: LeafBlock;
  theme: PageTheme;
  products: StoreProduct[];
  isMobile: boolean;
  onProductClick?: (handle: string) => void;
  productPageHandles?: ReadonlySet<string>;
  /** True only for the very first leaf of the very first row/column/section —
   *  the page's likely LCP element. Everything else is lazy. */
  priority?: boolean;
}> = ({ leaf, theme, products, isMobile, onProductClick, productPageHandles, priority }) => {
  const nt = asNewsletterTheme(theme);
  switch (leaf.type) {
    case 'text': {
      const b = leaf as TextBlock;
      // Shared with the builder's unselected text render (see leafDisplay).
      return (
        <div style={textBlockStyle(b) as CSSProperties}>
          <TextLeafContent block={b} theme={theme} />
        </div>
      );
    }
    // Legacy standalone `image`/`video` leaves are migrated to `library` on
    // every read (server: pageRoutes.js public routes; editor: SiteEditor's
    // openPage) — see imageLeafToLibrary/videoLeafToLibrary in
    // shared/pageBlocks.js — so this switch never actually sees those types
    // in practice. No case for them here; `library` is the single path.
    case 'library':
      return <LibraryLeaf block={leaf as LibraryBlock} priority={priority} onNavigate={onProductClick} />;
    case 'button': {
      const b = leaf as ButtonBlock;
      return (
        <div style={buttonBlockStyle(b) as CSSProperties}>
          <SmartLink target={b.href} onNavigate={onProductClick} style={buttonStyle(b, nt) as CSSProperties}>{b.label}</SmartLink>
        </div>
      );
    }
    case 'line': {
      const b = leaf as LineBlock;
      return <div style={lineBlockStyle(b) as CSSProperties}><hr style={lineStyle(b) as CSSProperties} /></div>;
    }
    case 'spacer':
      return <div style={spacerStyle(leaf as SpacerBlock) as CSSProperties} />;
    case 'hero':
      return <Hero hero={leaf as HeroBlock} theme={theme} onNavigate={onProductClick} />;
    case 'product-embed':
      return <ProductEmbed block={leaf as ProductEmbedBlock} theme={theme} products={products} isMobile={isMobile} onProductClick={onProductClick} />;
    case 'product-grid':
      return <ProductGrid block={leaf as ProductGridBlock} products={products} onProductClick={onProductClick} productPageHandles={productPageHandles} />;
    case 'mini-nav':
      return <MiniNavLeaf block={leaf as MiniNavBlock} onNavigate={onProductClick} />;
    default:
      return null;
  }
};

const Column: React.FC<{
  column: ColumnBlock;
  theme: PageTheme;
  products: StoreProduct[];
  isMobile: boolean;
  onProductClick?: (handle: string) => void;
  productPageHandles?: ReadonlySet<string>;
  /** True when this column is the first column of the first row of the
   *  section carrying priority — only its first leaf actually gets `priority`. */
  isPriorityCandidate?: boolean;
}> = ({ column, isPriorityCandidate, ...rest }) => (
  <div style={columnStyle(column, rest.isMobile)}>
    {column.blocks.map((leaf, leafIndex) => (
      <Leaf key={leaf.id} leaf={leaf} priority={!!isPriorityCandidate && leafIndex === 0} {...rest} />
    ))}
  </div>
);

const Row: React.FC<{
  row: RowBlock;
  theme: PageTheme;
  products: StoreProduct[];
  isMobile: boolean;
  onProductClick?: (handle: string) => void;
  productPageHandles?: ReadonlySet<string>;
  isPriorityCandidate?: boolean;
}> = ({ row, isPriorityCandidate, ...rest }) => (
  <div style={rowStyle(row, rest.isMobile)}>
    {row.columns.map((col, colIndex) => (
      <Column key={col.id} column={col} isPriorityCandidate={!!isPriorityCandidate && colIndex === 0} {...rest} />
    ))}
  </div>
);

const Section: React.FC<{
  section: SectionBlock;
  theme: PageTheme;
  products: StoreProduct[];
  isMobile: boolean;
  onProductClick?: (handle: string) => void;
  productPageHandles?: ReadonlySet<string>;
  /** True only for the first section on the page — gates which row/column/leaf
   *  can end up carrying `priority` (the likely LCP element). */
  isPriorityCandidate?: boolean;
}> = ({ section, isPriorityCandidate, ...rest }) => {
  const sectionVariants = section as SectionBlockWithVariant;
  const resolvedBgImage = useProgressiveTierBgImage({
    placeholder: sectionVariants.bgImageVariantPlaceholderSrc,
    small: sectionVariants.bgImageVariantSrc, large: sectionVariants.bgImageVariantLgSrc, original: section.bgImage,
    displayWidthPx: coverViewportPx(),
    // Same reasoning as Hero above — a section background can be full-bleed too.
    finalTier: 'original',
  });
  const sectionForStyle = resolvedBgImage !== section.bgImage ? { ...section, bgImage: resolvedBgImage } : section;
  return (
    <section style={sectionOuterStyle(sectionForStyle, rest.theme, rest.isMobile)}>
      <div style={sectionInnerStyle()}>
        {section.rows.map((row, rowIndex) => (
          <Row key={row.id} row={row} isPriorityCandidate={!!isPriorityCandidate && rowIndex === 0} {...rest} />
        ))}
      </div>
    </section>
  );
};

export const PageRenderer: React.FC<PageRendererProps> = ({ doc, onProductClick, isMobile: forcedMobile, productPageHandles, productGalleries }) => {
  const [products, setProducts] = useState<StoreProduct[]>([]);
  const autoMobile = useBelow(768);
  const isMobile = forcedMobile ?? autoMobile;

  useEffect(() => {
    let alive = true;
    fetchAllProducts().then((p) => { if (alive) setProducts(p); }).catch((e) => console.error('Load products failed:', e));
    return () => { alive = false; };
  }, []);

  return (
    <ProductGalleriesCtx.Provider value={productGalleries || {}}>
      <div style={pageRootStyle(doc.theme)}>
        {doc.sections.map((section, sectionIndex) => (
          <Section
            key={section.id}
            section={section}
            theme={doc.theme}
            products={products}
            isMobile={isMobile}
            onProductClick={onProductClick}
            productPageHandles={productPageHandles}
            isPriorityCandidate={sectionIndex === 0}
          />
        ))}
      </div>
    </ProductGalleriesCtx.Provider>
  );
};

export default PageRenderer;
